import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Star, Shield, Clock, Car, Wrench, Search, CreditCard, HandCoins, ChevronRight } from 'lucide-react';
import CarCard from '../components/CarCard';
import { cars as mockCars, services, testimonials, aboutData, brands, companyInfo } from '../data/mockData';
import { fetchCars, fetchHomepageSettings } from '../services/api';
import SEO from '../components/SEO';

const HOME_JSON_LD = [
  {
    "@context": "https://schema.org",
    "@type": "AutoDealer",
    "name": "BestCars — Comércio de Automóveis Lda",
    "description": "Stand premium em Castanheira do Ribatejo. Viaturas selecionadas com garantia, financiamento e atendimento personalizado. Mais de 15 anos de experiência.",
    "url": "https://bestcars.com.pt",
    "logo": "https://bestcars.com.pt/favicon.svg",
    "image": "https://bestcars.com.pt/og-image.png",
    "telephone": "+351932074479",
    "email": "bestcars.automovel@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Estrada da Vala Nº3, Stand Bestcars",
      "addressLocality": "Vala do Carregado",
      "postalCode": "2600-733",
      "addressRegion": "Vila Franca de Xira",
      "addressCountry": "PT"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 39.0167,
      "longitude": -8.9833
    },
    "openingHoursSpecification": [
      { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "10:00", "closes": "19:00" },
      { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Saturday"], "opens": "10:00", "closes": "17:00" }
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.7",
      "reviewCount": "41",
      "bestRating": "5"
    },
    "sameAs": [
      "https://www.facebook.com/bestcars19",
      "https://www.instagram.com/bestcars19stand"
    ],
    "priceRange": "€€"
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "BestCars",
    "url": "https://bestcars.com.pt",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://bestcars.com.pt/viaturas?search={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  }
];

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const youtubeBackgroundUrl = (videoId) => {
  const params = new URLSearchParams({
    autoplay: '1',
    mute: '1',
    controls: '0',
    loop: '1',
    playlist: videoId,
    playsinline: '1',
    rel: '0',
  });
  return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
};

const HeroSection = () => {
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [heroMedia, setHeroMedia] = useState({
    sourceType: 'upload',
    videoUrl: '/hero-video.mp4',
    posterUrl: '',
    youtubeId: '',
  });
  const videoRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetchHomepageSettings().then((settings) => {
      if (!cancelled && settings?.hero_video_url) {
        setHeroMedia({
          sourceType: settings.hero_video?.source_type || 'upload',
          videoUrl: settings.hero_video_url,
          posterUrl: settings.hero_poster_url || '',
          youtubeId: settings.hero_video?.youtube_id || '',
        });
      }
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const canUseVideo =
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
      !navigator.connection?.saveData;

    if (!canUseVideo) return;

    const schedule = window.requestIdleCallback || ((cb) => window.setTimeout(cb, 1200));
    const cancel = window.cancelIdleCallback || window.clearTimeout;
    const loadVideo = () => {
      const handle = schedule(() => setShouldLoadVideo(true));
      return () => cancel(handle);
    };

    if (document.readyState === 'complete') {
      return loadVideo();
    }

    let cancelIdle = null;
    const onLoad = () => {
      cancelIdle = loadVideo();
    };
    window.addEventListener('load', onLoad, { once: true });
    return () => {
      window.removeEventListener('load', onLoad);
      if (cancelIdle) cancelIdle();
    };
  }, []);

  useEffect(() => {
    if (!shouldLoadVideo || heroMedia.sourceType === 'youtube' || !videoRef.current) return;
    videoRef.current.load();
    videoRef.current.play().catch(() => {});
  }, [shouldLoadVideo, heroMedia.sourceType, heroMedia.videoUrl]);

  return (
  <section className="relative min-h-screen min-h-[100svh] flex items-center overflow-hidden">
    {/* Background */}
    <div className="absolute inset-0">
      <div className="absolute inset-0 bg-[#0a0a0a]" />
      <div className="absolute inset-0 opacity-40">
        {heroMedia.sourceType === 'youtube' && heroMedia.youtubeId ? (
          <>
            {heroMedia.posterUrl && (
              <img
                src={heroMedia.posterUrl}
                alt=""
                aria-hidden="true"
                className="h-full w-full object-cover"
              />
            )}
            {shouldLoadVideo && (
              <iframe
                src={youtubeBackgroundUrl(heroMedia.youtubeId)}
                title="Vídeo de apresentação BestCars"
                allow="autoplay; encrypted-media"
                tabIndex="-1"
                aria-hidden="true"
                className="pointer-events-none absolute left-1/2 top-1/2 h-[56.25vw] min-h-full w-[177.77777778vh] min-w-full -translate-x-1/2 -translate-y-1/2 border-0"
              />
            )}
          </>
        ) : (
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="none"
            poster={heroMedia.posterUrl || undefined}
            aria-hidden="true"
            className="w-full h-full object-cover"
          >
            {shouldLoadVideo && <source src={heroMedia.videoUrl} type="video/mp4" />}
          </video>
        )}
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/90 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-[#0a0a0a]/50" />
      {/* Grid pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)', backgroundSize: '60px 60px'}} />
    </div>

    {/* Accent shapes */}
    <div className="absolute top-1/4 left-0 w-96 h-96 bg-[#C41E3A]/5 rounded-full blur-[120px]" />
    <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-[#C41E3A]/3 rounded-full blur-[100px]" />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 lg:py-0">
      <div className="max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8"
        >
          <Star size={14} className="text-[#C41E3A]" />
          <span className="text-white/60 text-xs font-medium tracking-wider uppercase">Stand Premium em Castanheira do Ribatejo</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-[1.1] mb-6"
        >
          O seu próximo carro
          <br />
          <span className="text-[#C41E3A]">espera por si.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="text-white/50 text-lg sm:text-xl max-w-xl mb-10 leading-relaxed"
        >
          Viaturas selecionadas com rigor, garantia incluída e atendimento personalizado. A confiança que merece na compra do seu automóvel.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            to="/viaturas"
            className="group flex items-center justify-center gap-3 px-8 py-4 bg-[#C41E3A] hover:bg-[#a01830] text-white rounded-full font-semibold text-base transition-all duration-300 shadow-xl shadow-[#C41E3A]/20 hover:shadow-2xl hover:shadow-[#C41E3A]/30"
          >
            Ver Viaturas
            <ArrowRight size={18} className="transform group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            to="/contacto"
            className="flex items-center justify-center gap-3 px-8 py-4 bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 text-white rounded-full font-medium text-base transition-all duration-300"
          >
            Pedir Contacto
          </Link>
        </motion.div>

        {/* Trust badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="flex flex-wrap gap-6 mt-14"
        >
          {[
            { icon: Shield, text: 'Garantia Incluída' },
            { icon: Clock, text: 'Resposta Rápida' },
            { icon: Car, text: 'Viaturas Selecionadas' },
          ].map(({ icon: Icon, text }) => (
            <div key={text} className="flex items-center gap-2 text-white/30 text-sm">
              <Icon size={16} className="text-[#C41E3A]/60" />
              <span>{text}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </div>

    {/* Scroll indicator */}
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1 }}
      className="absolute bottom-10 left-1/2 -translate-x-1/2"
    >
      <div className="w-6 h-10 rounded-full border-2 border-white/20 flex items-start justify-center p-1.5">
        <motion.div
          animate={{ y: [0, 12, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-1.5 h-1.5 rounded-full bg-white/40"
        />
      </div>
    </motion.div>
  </section>
  );
};

const FeaturedCars = () => {
  const [featuredCars, setFeaturedCars] = useState([]);
  const [totalCars, setTotalCars] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCars = async () => {
      // Fetch featured + all active in parallel so the bottom CTA can show
      // a live count without an extra round-trip after the section paints.
      const [featuredData, allActive] = await Promise.all([
        fetchCars({ featured: true }),
        fetchCars(),
      ]);

      let pool = featuredData;
      if (!pool || pool.length === 0) pool = allActive;
      if (!pool || pool.length === 0) pool = mockCars.filter(c => c.featured).slice(0, 6);

      setFeaturedCars((pool || []).slice(0, 6));
      const totalSource = (allActive && allActive.length > 0) ? allActive : mockCars;
      setTotalCars(totalSource.length);
      setLoading(false);
    };
    loadCars();
  }, []);

  const featuredShown = Math.min(featuredCars.length, 6);
  const remaining = Math.max(totalCars - featuredShown, 0);

  return (
    <section className="py-24 bg-[#0a0a0a] relative">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeUp} className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-12">
          <div>
            <p className="text-[#C41E3A] text-sm font-semibold tracking-wider uppercase mb-2">Destaques</p>
            <h2 className="text-white text-3xl sm:text-4xl font-bold">Viaturas em Destaque</h2>
          </div>
          <Link
            to="/viaturas"
            className="flex items-center gap-2 text-white/50 hover:text-white text-sm font-medium group transition-colors"
          >
            Ver todas
            <ArrowRight size={16} className="transform group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            // Render 6 skeletons matching CarCard's exact height structure
            // (image + brand/model/version + 2x2 specs grid + price row).
            // This reserves the full grid height up-front and prevents CLS
            // when the real cards replace the placeholders.
            [...Array(6)].map((_, i) => (
              <div key={i} className="bg-[#111111] rounded-2xl border border-white/5 overflow-hidden">
                <div className="aspect-[16/10] bg-[#1a1a1a] animate-pulse" />
                <div className="p-5">
                  {/* brand + model + version block (matches CarCard mb-3) */}
                  <div className="mb-3">
                    <div className="h-3 w-16 bg-[#1a1a1a] rounded animate-pulse" />
                    <div className="h-5 w-32 bg-[#1a1a1a] rounded animate-pulse mt-1" />
                    <div className="h-3 w-24 bg-[#1a1a1a] rounded animate-pulse mt-1" />
                  </div>
                  {/* specs 2x2 grid (matches CarCard mb-4) */}
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <div className="h-4 bg-[#1a1a1a] rounded animate-pulse" />
                    <div className="h-4 bg-[#1a1a1a] rounded animate-pulse" />
                    <div className="h-4 bg-[#1a1a1a] rounded animate-pulse" />
                    <div className="h-4 bg-[#1a1a1a] rounded animate-pulse" />
                  </div>
                  {/* price + CTA row (matches CarCard border-t pt-4) */}
                  <div className="flex items-center justify-between pt-4 border-t border-white/5">
                    <div className="h-6 w-24 bg-[#1a1a1a] rounded animate-pulse" />
                    <div className="h-3 w-16 bg-[#1a1a1a] rounded animate-pulse" />
                  </div>
                </div>
              </div>
            ))
          ) : featuredCars.length > 0 ? (
            featuredCars.map((car, i) => (
              <CarCard key={car.id} car={car} index={i} />
            ))
          ) : null}
        </div>

        {/* Bottom CTA — anchors the end of the featured grid and pushes users
            into the full stock. Visually distinct from the hero's solid red
            pill: outlined gradient card with inverted (white) action button. */}
        {!loading && featuredCars.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="mt-16 relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#141414] via-[#0f0f0f] to-[#0a0a0a] px-6 py-10 sm:px-12 sm:py-14"
          >
            {/* Subtle radial highlight to draw the eye to the CTA */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 h-72 w-72 rounded-full bg-[#C41E3A]/15 blur-3xl"
            />

            <div className="relative flex flex-col items-center text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C41E3A]/10 border border-[#C41E3A]/20 mb-5">
                <Car size={14} className="text-[#C41E3A]" />
                <span className="text-[#C41E3A] text-xs font-semibold tracking-wider uppercase">Stock Completo</span>
              </div>

              <h3 className="text-white text-2xl sm:text-3xl font-bold leading-tight max-w-2xl">
                {remaining > 0
                  ? <>Há mais <span className="text-[#C41E3A]">{remaining} viaturas</span> à sua espera</>
                  : <>Explore toda a nossa <span className="text-[#C41E3A]">seleção em stock</span></>
                }
              </h3>

              <p className="text-white/50 text-sm sm:text-base mt-3 max-w-xl">
                Filtre por marca, combustível, preço e ano. Encontre a viatura ideal entre toda a nossa oferta — todas com garantia incluída.
              </p>

              <div className="mt-7">
                <Link
                  to="/viaturas"
                  className="group inline-flex items-center justify-center gap-2 px-9 py-4 bg-white hover:bg-white/90 text-[#0a0a0a] rounded-full font-semibold text-base transition-all duration-200 shadow-xl shadow-black/40 hover:shadow-2xl hover:scale-[1.02]"
                >
                  Explorar todo o stock
                  <ArrowRight size={18} className="transform group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

// `content-visibility: auto` skips render of off-screen sections and uses
// `contain-intrinsic-size` as a placeholder. `auto <fallback>` lets the
// browser remember real size after first paint, so CLS is minimal even on
// scroll. Estimated heights are conservative.
const offscreenSection = (h) => ({
  contentVisibility: 'auto',
  containIntrinsicSize: `auto ${h}px`,
});

const BrandsSection = () => (
  <section className="py-16 bg-[#0a0a0a] relative" style={offscreenSection(300)}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div {...fadeUp} className="text-center mb-10">
        <p className="text-white/30 text-sm font-medium tracking-wider uppercase">Marcas Disponíveis</p>
      </motion.div>
      <motion.div {...fadeUp} className="flex flex-wrap items-center justify-center gap-10 md:gap-16">
        {brands.map((brand) => (
          <div key={brand.name} className="group cursor-pointer">
            <div className="flex flex-col items-center gap-3 opacity-30 group-hover:opacity-70 transition-opacity duration-300">
              <span className="text-white text-sm font-medium tracking-wider">{brand.name}</span>
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  </section>
);

const iconMap = { Wrench, Search, CreditCard, HandCoins };

const ServicesSection = () => (
  <section className="py-24 bg-[#070707] relative" style={offscreenSection(1200)}>
    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div {...fadeUp} className="text-center mb-14">
        <p className="text-[#C41E3A] text-sm font-semibold tracking-wider uppercase mb-2">Os Nossos Serviços</p>
        <h2 className="text-white text-3xl sm:text-4xl font-bold">Soluções Completas</h2>
        <p className="text-white/40 mt-4 max-w-xl mx-auto">Oferecemos um conjunto de serviços pensados para facilitar a sua vida automóvel.</p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {services.map((service, i) => {
          const Icon = iconMap[service.icon];
          return (
            <Link to={service.link} key={service.id}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="group relative bg-[#111111] rounded-2xl p-6 border border-white/5 hover:border-[#C41E3A]/20 transition-all duration-500 overflow-hidden cursor-pointer h-full"
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#C41E3A] to-[#8B1428] transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                <div className="w-12 h-12 rounded-xl bg-[#C41E3A]/10 flex items-center justify-center mb-5 group-hover:bg-[#C41E3A]/20 transition-colors duration-300">
                  {Icon && <Icon size={22} className="text-[#C41E3A]" />}
                </div>
                <h3 className="text-white font-semibold text-base mb-2">{service.title}</h3>
                <p className="text-white/40 text-sm leading-relaxed">{service.description}</p>
                <div className="mt-4 flex items-center gap-1 text-[#C41E3A] text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  Saber mais <ChevronRight size={12} />
                </div>
              </motion.div>
            </Link>
          );
        })}
      </div>
    </div>
  </section>
);

const AboutTeaser = () => (
  <section className="py-24 bg-[#0a0a0a] relative overflow-hidden" style={offscreenSection(900)}>
    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-96 h-96 bg-[#C41E3A]/5 rounded-full blur-[150px]" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <motion.div {...fadeUp}>
          <p className="text-[#C41E3A] text-sm font-semibold tracking-wider uppercase mb-2">Sobre Nós</p>
          <h2 className="text-white text-3xl sm:text-4xl font-bold mb-6 leading-tight">{aboutData.title}</h2>
          <p className="text-white/50 leading-relaxed mb-8">{aboutData.paragraphs[0]}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-8">
            {aboutData.stats.map((stat) => (
              <div key={stat.label}>
                <p className="text-[#C41E3A] font-bold text-2xl">{stat.value}</p>
                <p className="text-white/40 text-xs mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
          <Link
            to="/sobre"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm font-medium group transition-colors"
          >
            Conhecer a nossa história
            <ChevronRight size={16} className="transform group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative"
        >
          <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
            <img
              src="https://customer-assets.emergentagent.com/job_2afd108d-2129-4230-9456-c5c427488476/artifacts/5z17qowq_195330405_827126831268028_6309585114239873631_n.jpg"
              alt="BestCars Showroom"
              width="900"
              height="675"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/60 to-transparent" />
          </div>
          <div className="absolute -bottom-4 -left-4 bg-[#C41E3A] rounded-xl px-6 py-4 shadow-xl">
            <p className="text-white font-bold text-2xl">15+</p>
            <p className="text-white/80 text-xs">Anos de Experiência</p>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

const TestimonialsSection = () => {
  const [currentPage, setCurrentPage] = React.useState(0);
  const itemsPerPage = 4;
  const totalPages = Math.ceil(testimonials.length / itemsPerPage);
  const visibleTestimonials = testimonials.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);

  // Auto-slide every 10 seconds
  React.useEffect(() => {
    if (totalPages <= 1) return;
    const interval = setInterval(() => {
      setCurrentPage(prev => (prev + 1) % totalPages);
    }, 10000);
    return () => clearInterval(interval);
  }, [totalPages]);

  return (
    <section className="py-24 bg-[#070707] relative" style={offscreenSection(1200)}>
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeUp} className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6 mb-14">
          <div>
            <p className="text-[#C41E3A] text-sm font-semibold tracking-wider uppercase mb-2">Testemunhos</p>
            <h2 className="text-white text-3xl sm:text-4xl font-bold">O Que Dizem os Nossos Clientes</h2>
          </div>
          {/* Google Rating Badge */}
          <div className="flex items-center gap-3 bg-[#111111] rounded-2xl px-5 py-3 border border-white/5">
            <div className="flex flex-col items-center">
              <span className="text-white font-bold text-2xl leading-none">{companyInfo.googleRating}</span>
              <div className="flex gap-0.5 mt-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={12} className={i < Math.round(companyInfo.googleRating) ? "fill-amber-400 text-amber-400" : "fill-white/10 text-white/10"} />
                ))}
              </div>
            </div>
            <div className="h-10 w-px bg-white/10" />
            <div>
              <p className="text-white/60 text-xs">{companyInfo.googleReviews} avaliações</p>
              <div className="flex items-center gap-1 mt-0.5">
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                <span className="text-white/40 text-[10px]">Google</span>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {visibleTestimonials.map((t, i) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              className="bg-[#111111] rounded-2xl p-6 border border-white/5 hover:border-white/10 transition-all duration-300"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#C41E3A]/20 flex items-center justify-center text-[#C41E3A] font-bold text-sm">
                  {t.initial}
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">{t.name}</p>
                  <div className="flex items-center gap-1">
                    <svg viewBox="0 0 24 24" className="w-3 h-3" xmlns="http://www.w3.org/2000/svg"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                    <span className="text-white/30 text-[10px]">Google</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-0.5 mb-3">
                {[...Array(t.rating)].map((_, j) => (
                  <Star key={j} size={12} className="fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-white/60 text-sm leading-relaxed">"{t.text}"</p>
            </motion.div>
          ))}
        </div>

        {/* Pagination dots */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-10">
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i)}
                className={`transition-all duration-300 rounded-full ${i === currentPage ? 'w-8 h-2 bg-[#C41E3A]' : 'w-2 h-2 bg-white/20 hover:bg-white/40'}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const CTASection = () => (
  <section className="py-24 bg-[#0a0a0a] relative overflow-hidden" style={offscreenSection(500)}>
    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#C41E3A]/5 to-transparent" />
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
      <motion.div {...fadeUp}>
        <h2 className="text-white text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">Pronto para encontrar o seu carro ideal?</h2>
        <p className="text-white/50 text-lg mb-10 max-w-2xl mx-auto">Visite-nos ou entre em contacto. Teremos todo o gosto em ajudá-lo a encontrar a viatura perfeita.</p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/viaturas"
            className="group flex items-center gap-3 px-8 py-4 bg-[#C41E3A] hover:bg-[#a01830] text-white rounded-full font-semibold transition-all duration-300 shadow-xl shadow-[#C41E3A]/20"
          >
            Explorar Viaturas
            <ArrowRight size={18} className="transform group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            to="/contacto"
            className="flex items-center gap-3 px-8 py-4 bg-white/5 border border-white/10 hover:bg-white/10 text-white rounded-full font-medium transition-all duration-300"
          >
            Fale Connosco
          </Link>
        </div>
      </motion.div>
    </div>
  </section>
);

const HomePage = () => (
  <>
    <SEO
      title="Stand de Automóveis Premium em Castanheira do Ribatejo"
      description="Stand premium em Castanheira do Ribatejo. Viaturas selecionadas com garantia, financiamento e atendimento personalizado. Avaliação 4.7 ★ no Google. Mais de 15 anos de experiência."
      canonical="/"
      jsonLd={HOME_JSON_LD}
    />
    <HeroSection />
    <FeaturedCars />
    <BrandsSection />
    <ServicesSection />
    <AboutTeaser />
    <TestimonialsSection />
    <CTASection />
  </>
);

export default HomePage;
